import React, { DetailedHTMLProps, ReactNode, useEffect } from 'react';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import FormSectionTitle, { FormSectionTitleProps } from './FormSectionTitle';
import FormGrid, { FormGridProps } from './FormGrid';
import { Collapse } from '@material-ui/core';
import { Add, Remove } from '@material-ui/icons';
import IconButton from '@material-ui/core/IconButton';
import clsx from 'clsx';
import { emitter, Events } from '../../../scripts/events';

const useStyles = makeStyles(() =>
  createStyles({
    section: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: 32,
      gap: 8,
      // On paper the generous on-screen spacing pushes these forms onto more pages than they
      // need, so the gaps between sections and under each title close up.
      '@media print': {
        marginBottom: 10,
        gap: 3,
      },
    },
  }),
);

type FormSectionProps = {
  title?: string;
  largeTitle?: boolean;
  extraTitleElement?: ReactNode;
  noGrid?: boolean;
  formGridProps?: FormGridProps;
  collapsible?: boolean;
  defaultOpen?: boolean;
} & DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export default function FormSection(props: FormSectionProps) {
  const {
    title,
    extraTitleElement = null,
    noGrid,
    children,
    formGridProps,
    largeTitle,
    collapsible,
    defaultOpen,
    className,
    ...rest
  } = props;
  const classes = useStyles();

  const [open, setOpen] = React.useState(!!defaultOpen);

  const expandRow = () => setOpen(true);
  const collapseRow = () => setOpen(false);

  useEffect(() => {
    emitter.on(Events.ExpandRows, expandRow);

    emitter.on(Events.CloseRows, collapseRow);

    return () => {
      emitter.removeListener(Events.ExpandRows, expandRow);
      emitter.removeListener(Events.CloseRows, collapseRow);
    };
  }, []);

  const titleElement = (props?: FormSectionTitleProps) =>
    title ? (
      <FormSectionTitle large={largeTitle} {...props}>
        {title}
      </FormSectionTitle>
    ) : null;

  const contentElement = noGrid ? (
    <>{children}</>
  ) : (
    <FormGrid {...formGridProps}>{children}</FormGrid>
  );

  if (collapsible) {
    return (
      <>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: largeTitle ? 24 : 16,
          }}
          className={className}
        >
          {titleElement({ style: { marginBottom: 0 } })}
          {extraTitleElement}
          <IconButton onClick={() => setOpen((prev) => !prev)} style={{ padding: 0 }}>
            {open ? <Remove /> : <Add />}
          </IconButton>
        </div>
        <Collapse in={open}>
          <div className={classes.section} {...rest}>
            {contentElement}
          </div>
        </Collapse>
      </>
    );
  }

  return (
    <div className={clsx(classes.section, className)} {...rest}>
      {titleElement()}
      {extraTitleElement}
      {contentElement}
    </div>
  );
}
